/**
 * Serializes an editor's saves so a tab cannot race itself.
 *
 * Both spec editors — pipelines and pages — hold an ADR-0159 concurrency token
 * that is learned only from a save's RESPONSE. That makes overlap, not
 * ordering, the hazard: a second save started before the first response lands
 * carries the token the first is about to replace, the hub correctly refuses
 * it, and the conflict banner names this very tab's own write from
 * milliseconds earlier ("Brian Holmes … at <the gesture's moment>"). Both
 * editors have many save triggers — debounced autosave, blur handlers,
 * select-change and remove and undo/redo flushes, drag/resize commits — and
 * any two inside one request's latency used to race. A pointer gesture is the
 * usual culprit because its opening pointerdown blurs whatever field had
 * focus, so the gesture fires a save at both ends of itself.
 *
 * `run` serializes: a caller that arrives while a task is in flight WAITS for
 * it, then either short-circuits (`skipAfterWait`) or runs with the token the
 * predecessor's response remembered. `skipAfterWait` is where a caller decides
 * its work is moot — the predecessor already saved everything, or a refusal is
 * now on screen and re-sending would only be refused again.
 *
 * Callers: `routes/OperationEditor.tsx` (2026-08-22), `routes/BuildPageEditor.tsx`
 * (2026-08-24).
 */
export function createSingleFlight<T>() {
  let current: Promise<T> | null = null;
  return {
    inFlight(): boolean {
      return current !== null;
    },
    async run(task: () => Promise<T>, opts?: { skipAfterWait?: () => T | null }): Promise<T> {
      while (current) {
        // A predecessor's failure is its caller's to handle — never this one's.
        await current.catch(() => {});
        const skip = opts?.skipAfterWait?.();
        if (skip !== null && skip !== undefined) return skip;
      }
      const flight = task();
      current = flight;
      try {
        return await flight;
      } finally {
        current = null;
      }
    },
  };
}
